import React, { useMemo, useRef, useState } from "react";
import { useMoney } from "../../../hooks/useMoney";
import { VoucherStatsItem } from "../types";
import { MISChart } from "../components/MISChart";
import { chartTheme } from "../chartTheme";
import { SectionToggle, SectionViewMode } from "../components/SectionToggle";
import { TablePagination } from "../components/TablePagination";
import { ExportButton } from "../components/ExportButton";
import { downloadChartImage, exportToCsv } from "../components/exportUtils";
import type { Chart as ChartJS } from "chart.js";

interface VoucherStatsSectionProps {
  title: string;
  data: VoucherStatsItem[];
}

export const VoucherStatsSection: React.FC<VoucherStatsSectionProps> = ({ title, data }) => {
  const { code, number } = useMoney();
  const [mode, setMode] = useState<SectionViewMode>("graph");
  const [page, setPage] = useState(1);
  const chartRef = useRef<ChartJS | null>(null);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const visible = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return data.slice(startIndex, startIndex + pageSize);
  }, [page, data]);

  const handleExportChart = () => {
    downloadChartImage(chartRef.current, "voucher-stats-graph.png");
  };

  const handleExportTable = () => {
    exportToCsv(
      "voucher-stats-table.csv",
      ["Date", "Created", "Activated", "Used", "Created Amt", "Used Amt", "Expired Amt"],
      data.map((row) => [
        row.date,
        row.created,
        row.activated,
        row.used,
        row.createdAmt,
        row.usedAmt,
        row.expiredAmt,
      ])
    );
  };

  return (
    <div className="overflow-hidden border border-cardborder bg-white shadow-sm">
      <div className="border-b border-cardborder bg-gray-50 px-6 py-4">
        <SectionToggle
          title={title}
          subtitle="Created, activated, used and amount-wise metrics by date"
          mode={mode}
          onChange={setMode}
          actions={
            <ExportButton
              label={mode === "graph" ? "Export PNG" : "Export CSV"}
              onClick={mode === "graph" ? handleExportChart : handleExportTable}
            />
          }
        />
      </div>
      <div className="px-6 py-5">
      {mode === "graph" ? (
        (() => {
          const filtered = data.filter(
            (row) => row.created > 0 || row.activated > 0 || row.used > 0
          );
          return filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No data to display</p>
          ) : (
            <MISChart
              ref={chartRef}
              type="bar"
              labels={filtered.map((row) => row.date)}
              datasets={[
                {
                  label: "Created",
                  data: filtered.map((row) => row.created),
                  borderColor: chartTheme.brand,
                  backgroundColor: chartTheme.brandLight,
                },
                {
                  label: "Activated",
                  data: filtered.map((row) => row.activated),
                  borderColor: chartTheme.teal,
                  backgroundColor: chartTheme.tealLight,
                },
                {
                  label: "Used",
                  data: filtered.map((row) => row.used),
                  borderColor: chartTheme.indigo,
                  backgroundColor: chartTheme.indigoLight,
                },
              ]}
            />
          );
        })()
      ) : (
        <div className="overflow-hidden border border-cardborder">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-right font-semibold">Created</th>
                  <th className="px-3 py-2 text-right font-semibold">Activated</th>
                  <th className="px-3 py-2 text-right font-semibold">Used</th>
                  <th className="px-3 py-2 text-right font-semibold">Created Amt</th>
                  <th className="px-3 py-2 text-right font-semibold">Used Amt</th>
                  <th className="px-3 py-2 text-right font-semibold">Expired Amt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visible.map((row, idx) => (
                  <tr
                    key={row.date}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} transition-colors hover:bg-brand/5`}
                  >
                    <td className="px-3 py-2">{row.date}</td>
                    <td className="px-3 py-2 text-right">{row.created}</td>
                    <td className="px-3 py-2 text-right">{row.activated}</td>
                    <td className="px-3 py-2 text-right">{row.used}</td>
                    <td className="px-3 py-2 text-right">
                      {code} {number(row.createdAmt)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {code} {number(row.usedAmt)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {code} {number(row.expiredAmt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={data.length}
            pageSize={pageSize}
            itemLabel="days"
            onPageChange={setPage}
          />
        </div>
      )}
      </div>
    </div>
  );
};
